import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button } from 'app/components/Common/Button'
import { styles } from 'app/theme'
import useLockedBody from 'app/utils/useLockedBody'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo, useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'

import { Modal } from './Modal'

export interface Props {
  description?: string
  ID?: string
  label?: string
  languageCode: string
  phone?: string
  title?: string
  URL?: string
}

export const BlockCTA = memo(function BlockCTA({
  description,
  ID,
  label,
  languageCode,
  phone,
  title,
  URL,
}: Props) {
  if (!title) {
    return null
  }

  const [_locked, setLocked] = useLockedBody()
  const [modalStatus, setModalStatus] = useState(false)

  useEffect(() => {
    document.addEventListener('keydown', function (e) {
      switch (e.key) {
        case 'Escape':
          setLocked(false)
          setModalStatus(false)
          break
      }
    })
  }, [])

  return (
    <Container>
      {label ? <Label tag="h2" text={label} /> : null}

      {title ? <Title tag="h3" text={title} /> : null}

      {description ? (
        <FadeInUp>
          <Description dangerouslySetInnerHTML={{ __html: description }} />
        </FadeInUp>
      ) : null}

      {phone || ID || URL ? (
        <Buttons>
          {ID ? (
            <FadeInUp>
              <CTA
                label={useVocabularyData('book-a-table', languageCode)}
                onClick={() => {
                  setModalStatus(true)
                  setLocked(true)
                }}
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter') {
                    setModalStatus(true)
                    setLocked(true)
                  }
                }}
                rel="noopener"
                target="_blank"
              />
            </FadeInUp>
          ) : null}

          {URL ? (
            <FadeInUp>
              <CTA
                label={useVocabularyData('book-beach', languageCode)}
                rel="noopener"
                target="_blank"
                URL={URL}
              />
            </FadeInUp>
          ) : null}

          {phone ? (
            <FadeInUp>
              <CTA
                label={useVocabularyData('call', languageCode)}
                onClick={() => {
                  if (isMobile) {
                    typeof window.gtag !== 'undefined' &&
                      window.gtag('event', 'Click', {
                        event_category: 'Website',
                        event_label: 'Phone Number',
                      })
                  }
                }}
                target="tel"
                URL={phone}
              />
            </FadeInUp>
          ) : null}
        </Buttons>
      ) : null}

      {ID ? (
        <Modal
          ID={ID}
          modalStatus={modalStatus}
          onClick={() => {
            setLocked(false)
            setModalStatus(false)
          }}
        />
      ) : null}
    </Container>
  )
})

const Container = styled.section`
  margin-top: 11.25rem;
  padding: 0 2rem;
  text-align: center;

  @media (max-width: 1199px) {
    margin-top: 5.25rem;
  }
`

const Label = styled(AnimatedTitle)`
  ${styles.label}

  margin-bottom: 1.5rem;

  @media (max-width: 1199px) {
    margin-bottom: 1.5rem;
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.large}

  max-width: 53rem;
  margin: auto;

  @media (max-width: 1199px) {
    max-width: none;
  }
`

const Description = styled.div`
  ${styles.description}

  margin-top: 0.75rem;
`

const Buttons = styled.div`
  margin-top: 3rem;

  > div {
    &:nth-of-type(2) {
      margin-top: 0.75rem;
    }
  }

  @media (max-width: 1199px) {
    margin-top: 2rem;
  }
`

const CTA = styled(Button)``
